/**
 * i18next initialization — configures the i18n system for the entire app.
 *
 * Imported as a side-effect in index.tsx. When REACT_APP_I18N_UI_ENABLED
 * is not "true", forces English and skips language detection — the app
 * behaves identically to before i18n was added.
 *
 * Architecture (RFC03 §4.6):
 * - ICU MessageFormat for CLDR-correct pluralization
 * - HTTP backend for lazy-loading translation JSON files
 * - Browser language detection (localStorage → navigator)
 * - XSS protection via escapeValue: true
 */

import i18n from "i18next";
import ICU from "i18next-icu";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import { clientEnv } from "src/config/clientEnv";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from "./constants";

const i18nEnabled = clientEnv.i18nUiEnabled;

i18n
  .use(ICU)
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // When feature flag is off, force English and skip detection
    ...(i18nEnabled ? {} : { lng: DEFAULT_LOCALE }),

    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],

    defaultNS: "common",
    ns: [
      "assessments",
      "auth",
      "calendars",
      "comments",
      "common",
      "dashboard",
      "discussions",
      "eSignatures",
      "employees",
      "errors",
      "exports",
      "invites",
      "landing",
      "learningPaths",
      "legal",
      "memos",
      "nav",
      "onboarding",
      "policies",
      "reports",
      "rotations",
      "schedules",
      "settings",
      "support",
      "timeOffRequests",
      "todos",
      "validation",
      "videos",
    ],

    interpolation: {
      escapeValue: true,
    },

    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },

    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "clearpath_locale",
    },

    react: {
      useSuspense: false,
    },
  });

export default i18n;
